import { Camera, Image, Sparkles, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { BackgroundEffect } from '../hooks/useDailyVideo'

interface SettingsDialogProps {
  stream: MediaStream | null
  devices: MediaDeviceInfo[]
  deviceId: string
  backgroundEffect: BackgroundEffect
  effectsAvailable: boolean
  onSelectDevice: (deviceId: string) => Promise<void>
  onBackgroundEffect: (effect: BackgroundEffect, source?: ArrayBuffer) => Promise<boolean>
  onClose: () => void
}

function Preview({ stream }: { stream: MediaStream | null }) {
  const video = useRef<HTMLVideoElement>(null)
  useEffect(() => {
    if (!video.current) return
    video.current.srcObject = stream
    void video.current.play().catch(() => undefined)
  }, [stream])
  return stream ? <video ref={video} autoPlay muted playsInline /> : <span><Camera /></span>
}

export function SettingsDialog({ stream, devices, deviceId, backgroundEffect, effectsAvailable, onSelectDevice, onBackgroundEffect, onClose }: SettingsDialogProps) {
  const [section, setSection] = useState<'devices' | 'background'>('devices')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [onClose])

  const chooseEffect = async (effect: BackgroundEffect, source?: ArrayBuffer) => {
    if (!effectsAvailable || busy) return
    setBusy(true)
    await onBackgroundEffect(effect, source)
    setBusy(false)
  }

  return (
    <div className="modal-backdrop settings-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
      <section className="settings-dialog" role="dialog" aria-modal="true" aria-label="Settings">
        <header><strong>Settings</strong><button aria-label="Close settings" onClick={onClose}><X /></button></header>
        <div className="settings-layout">
          <nav>
            <button className={section === 'devices' ? 'active' : ''} onClick={() => setSection('devices')}>Audio & video</button>
            <button className={section === 'background' ? 'active' : ''} onClick={() => setSection('background')}>Background effects</button>
          </nav>
          <div className="settings-content">
            {section === 'devices' ? <>
              <div className="settings-preview"><Preview stream={stream} /></div>
              <label><span><Camera size={15} /> Camera</span><select value={deviceId} disabled={!devices.length} onChange={(event) => void onSelectDevice(event.target.value)}>{!devices.length && <option value="">No camera</option>}{devices.map((device, index) => <option value={device.deviceId} key={device.deviceId}>{device.label || `Camera ${index + 1}`}</option>)}</select></label>
            </> : <div className="background-options">
              <button className={backgroundEffect === 'none' ? 'active' : ''} disabled={!effectsAvailable || busy} onClick={() => void chooseEffect('none')}><span className="effect-none" /><strong>None</strong></button>
              <button className={backgroundEffect === 'blur' ? 'active' : ''} disabled={!effectsAvailable || busy} onClick={() => void chooseEffect('blur')}><span className="effect-blur"><Sparkles /></span><strong>Blur</strong></button>
              <label className={!effectsAvailable || busy ? 'disabled' : ''}><span className="effect-image"><Image /></span><strong>Image</strong><input disabled={!effectsAvailable || busy} accept="image/jpeg,image/png" type="file" onChange={(event) => { const file = event.target.files?.[0]; if (file) void file.arrayBuffer().then((source) => chooseEffect('image', source)) }} /></label>
            </div>}
          </div>
        </div>
      </section>
    </div>
  )
}
